import React, {useEffect, useRef, useState} from "react";
import {Button, TextField, Tooltip} from "@material-ui/core";
import HelpIcon from '@material-ui/icons/Help';
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {Alert, Color} from "@material-ui/lab";
import {UIDialog} from "../../../../ui/feedback/UIDialog";
import {useOktaAuth} from '../../../../okta-react';
import {strings} from '../../i18n/strings';
import {Compellable} from "../indexes/Indexes";
import {RecordData} from "../../presenters/pages/DefaultLeadTimePage";
import {upsertDefaultLeadTime} from "../repository/DefaultLeadTimeRepository";
import {AsyncAutocompleteSelectComponent, DropDownValue} from "../components/AsyncAutocompleteSelect";

const useStyles = makeStyles((theme: Theme) => {
    return createStyles({
        root: {
            display: 'flex',
            alignItems: 'center',
            width: '550px'
        },
        tooltip: {
            marginLeft: '15px'
        },
        sliderRoot: {
            display: 'flex',
            alignItems: 'center',
            width: '550px',
            margin: '10px 0 10px 0'
        },
        sliderTooltip: {
            marginLeft: 'auto'
        },
        displayFlex: {
            display: 'flex',
            alignItems: 'center',
            width: '550px'
        },
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
            maxWidth: 300,
        },
        chips: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        chip: {
            margin: 2,
        },
        noLabel: {
            marginTop: theme.spacing(3),
        }
    })
});

const wordingDefaultLeadTime = strings.page.defaultLeadTime;

export type EditFormModalOptions = {
    title: string;
    open: boolean;
    applyLabel: string;
    cancelLabel: string;
    applyAndResetLabel?: string;
    onClose: () => void;
    onSuccess: (shouldReset: boolean) => Promise<void>;
};

type EditFormProps = {
    constraints: Compellable;
    record?: RecordData | undefined;
    modalOptions: EditFormModalOptions;
};

type ValidationFeedback = {
    [key in keyof Compellable]: boolean;
};

export const EditForm = function ({
                                      constraints,
                                      record,
                                      modalOptions
                                  }: EditFormProps) {

    const [isRecordChanged, setIsRecordChanged] = useState(false);

    const initImmediateValidationFeedback = (resetOnSubmitAndContinue: boolean) => {
        const result: any = {};
        tableFields.map((field) => (result[field] = !!record || (!constraints[field].required || !constraints[field].editable || resetOnSubmitAndContinue)));
        return result as ValidationFeedback;
    };

    const initFieldChangeState = () => {
        const isPristine = true;
        const result: any = {};
        tableFields.map((field) => (result[field] = record?.record ? !isPristine : isPristine));
        return result as ValidationFeedback;
    };

    const tableFields = Object.keys(constraints).filter((key) => key !== 'id');
    const [isFieldPristine, setIsFieldPristine] = useState(initFieldChangeState());
    const classes = useStyles();
    const [isOk, setIsOk] = useState<{ [key: string]: any }>(initImmediateValidationFeedback(false));
    const $form = useRef<HTMLFormElement>(null);
    const [internalNotification, setInternalNotification] = useState<
        { severity: Color; message: string } | undefined
    >();
    const {authState} = useOktaAuth();
    const [isPristine, setIsPristine] = useState(true);
    const [store, setStore] = React.useState<string | null>();
    const [shipmentMethod, setShipmentMethod] = React.useState<string | null>();
    const [defaultLeadTime, setDefaultLeadTime] = React.useState<number>(0);

    const loadData = (recordData?: RecordData | undefined) =>  {
        setStore(record?.record.store);
        setShipmentMethod(record?.record.shipmentMethod);
        const field = 'defaultLeadTime';
        const isRequired = constraints[field].required;
        const isEditable = constraints[field].editable;
        const type = constraints[field].type;

        let defaultLeadTime = record? Number(record.record.defaultLeadTime) : 0;
        setIsOk((prev) => ({
            ...prev,
            [field]: fieldValidation(type, isRequired, isEditable, defaultLeadTime),
        }));
        setIsFieldPristine((prev: any) => ({
            ...prev,
            [field]: false,
        }));
        setIsRecordChanged(true);
        isPristine && setIsPristine(false);
        setInternalNotification(undefined);
        setDefaultLeadTime(defaultLeadTime);
    };

    useEffect(() => {
        loadData(record);
    }, [record]);

    const resetStates = (resetOnSubmitAndContinue: boolean) => {
        if (!resetOnSubmitAndContinue) {
            setIsOk(initImmediateValidationFeedback(!resetOnSubmitAndContinue));
            setIsFieldPristine(initFieldChangeState());
        }
        setInternalNotification(undefined);
    };

    const handleModalClose = () => {
        modalOptions.onClose();
        resetStates(false);
        setStore(null);
        setShipmentMethod(null);
        setDefaultLeadTime(0);
    }

    /**
     * Row edition
     */
    const handleEditFormSubmit = async (form: any) => {
        const feedbackWordings = record ?
            wordingDefaultLeadTime.datatable.action.edit
            : wordingDefaultLeadTime.datatable.action.add;
        const requestBody = {
            toUpdate: record?.record,
            updateBy: form
        }
        await upsertDefaultLeadTime(authState!, requestBody);

        return feedbackWordings.confirmationFeedbackMessage;
    };

    const handleFormSubmit = (shouldReset: boolean) => {
        const form = {
            store,
            shipmentMethod,
            defaultLeadTime
        };

        setInternalNotification(undefined);
        (async () => {
            try {
                const message = await handleEditFormSubmit(form);
                await setInternalNotification({severity: 'success', message});
                modalOptions.onSuccess(shouldReset);
                if (!shouldReset) {
                    setInternalNotification(undefined);
                    setStore(null);
                    setShipmentMethod(null);
                    setDefaultLeadTime(0);
                }
            } catch (error: any) {
                const errorMessage: string = error.message;
                setInternalNotification({severity: 'error', message: errorMessage});
            }
        })();
    };

    const onParentChangeValue = (parentId: string | number, parentValue: DropDownValue | null) => {
        if (parentId === 'edit-store' && parentValue) {
            setStore(parentValue.key);
            handleImmediateValidationDropDownList('store', parentValue)
        }
        if (parentId === 'edit-shipmentMethod' && parentValue) {
            setShipmentMethod(parentValue.key);
            handleImmediateValidationDropDownList('shipmentMethod', parentValue)
        }
    }

    const handleImmediateValidationDropDownList = (
        field: keyof Compellable,
        dropdownValue?: DropDownValue | null
    ) => {
        const isRequired = constraints[field].required;
        const isEditable = constraints[field].editable;
        const type = constraints[field].type;

        setIsOk((prev) => ({
            ...prev,
            [field]: fieldValidation(type, isRequired, isEditable, dropdownValue),
        }));

        setIsFieldPristine((prev) => ({
            ...prev,
            [field]: false,
        }));
        setIsRecordChanged(true);
        isPristine && setIsPristine(false);
        setInternalNotification(undefined);
    };

    const handleImmediateValidation = (
        field: keyof Compellable,
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const isRequired = constraints[field].required;
        const isEditable = constraints[field].editable;
        const type = constraints[field].type;
        const value = e.target.value.trim();

        setIsOk((prev) => ({
            ...prev,
            [field]: fieldValidation(type, isRequired, isEditable, value),
        }));

        setIsFieldPristine((prev: any) => ({
            ...prev,
            [field]: false,
        }));
        setIsRecordChanged(true);
        isPristine && setIsPristine(false);
        setInternalNotification(undefined);
        setDefaultLeadTime(+value);
    };

    const fieldValidation = (type: String, isRequired: boolean, isEditable: boolean, value: any) => {
        if (isRequired) { //only for mandatory fields
            //validation ok if value not null, not undefined nor an empty string
            return ![null, undefined, ""].includes(value);
        }
        return isEditable;

    };

    const cancelButtonOnClick = () => {
        modalOptions.onClose();
        setInternalNotification(undefined);
        resetStates(false);
        setStore(null);
        setShipmentMethod(null);
        setDefaultLeadTime(0);
    }
    const getDefaultValue = (field: String | number) => {
        if (record) {
            if (field === 'store') {
                return {
                    key: String(record.record[String('store')]),
                    value: String(record.record[String('store')]) + ' - ' + String(record.record[String('storeDescription')])
                };
            }
            if (field === 'shipmentMethod') {
                return {
                    key: String(record.record[String('shipmentMethod')]),
                    value: String(record.record[String('shipmentMethod')]) + ' - ' + String(record.record[String('shipmentMethodDescription')])
                };
            }
        }
        return null;
    }

    const buildModalActions = () => {
        let isPristine = tableFields.every((field) => {
            return isFieldPristine[field];
        })
        const isButtonDisabled = (isPristine || !isRecordChanged) || tableFields.some((field) => !isOk?.[field]);

        return [
            <Button key="modal-cancel" onClick={cancelButtonOnClick}>
                {modalOptions.cancelLabel}
            </Button>,
            modalOptions.applyAndResetLabel ? (
                <Button key="modal-apply-reset" onClick={handleFormSubmit.bind(null, true)} disabled={isButtonDisabled}>
                    {modalOptions.applyAndResetLabel}
                </Button>
            ) : null,
            <Button
                key="modal-validate"
                onClick={() => handleFormSubmit(false)}
                variant="contained"
                color="primary"
                disabled={isButtonDisabled}
                disableElevation
            >
                {modalOptions.applyLabel}
            </Button>,
        ];
    };

    return (
        <UIDialog
            open={modalOptions.open}
            onClose={handleModalClose}
            title={modalOptions.title}
            actions={buildModalActions()}
        >
            {internalNotification && (
                <Alert severity={internalNotification.severity}>{internalNotification.message}</Alert>
            )}
            <form ref={$form} autoComplete={`off`}>
                {tableFields.map((field: keyof Compellable) => {
                    const isRequired = constraints[field].required;
                    const information = constraints[field].information;
                    const isEditable = constraints[field].editable;
                    const reference = constraints[field].reference;
                    const type = constraints[field].type;
                    const displayName = constraints[field].displayName;
                    return (
                        <div>
                            {isEditable && (type !== "number") && (
                                <div className={classes.displayFlex}>
                                    <AsyncAutocompleteSelectComponent
                                        id={`edit-${field}`}
                                        key={field}
                                        autocompleteContainerStyle={{
                                            'flexGrow': 1
                                        }}
                                        reference={reference}
                                        required={isRequired}
                                        information={information}
                                        label={displayName}
                                        defaultValue={getDefaultValue(field)}
                                        onParentChangeValue={onParentChangeValue}
                                    />

                                    {information && (
                                        <Tooltip title={information} className={classes.tooltip}>
                                            <HelpIcon/>
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                            {isEditable && isRequired && (type === "number") && (
                                <div className={classes.root}>
                                    <TextField
                                        type="number"
                                        key={field}
                                        required={isRequired}
                                        InputProps={{
                                            inputProps: {
                                                min: 0
                                            }
                                        }}
                                        onInput={(e) => {
                                            (e.target as HTMLInputElement).value = Math
                                                .max(0, parseInt((e.target as HTMLInputElement).value))
                                                .toString()
                                                .slice(0, 2)}}
                                        fullWidth
                                        error={!isFieldPristine[field] ? !isOk[field] : false}
                                        margin="normal"
                                        size="small"
                                        onChange={(e) => handleImmediateValidation(field, e)}
                                        id={`edit-${field}`}
                                        label={displayName}
                                        variant="outlined"
                                        defaultValue={record ? record.record[field] : 0}
                                    />
                                    {information && (
                                        <Tooltip title={information} className={classes.tooltip}>
                                            <HelpIcon/>
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </form>
        </UIDialog>
    );
};